import {
  useAuditsListQuery,
  useChargeCategoriesAuditQuery,
  useDeleteBillIdsQuery,
  useDeleteDepositDocumentIdsQuery,
  // useDeleteDocumentIdsQuery,
  useDeleteProductionIdsQuery,
  useDeleteReservedResourceIdsQuery,
  useReservationStatesQuery,
  useResourceTypeAuditQuery,
  useUsersQuery,
  useVatsAuditQuery,
} from 'src/api/api.generated'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { getChanges } from './get-changes-from-audit'

export default function ReservationAuditCRUD({
  propertyId,
  tenantId,
  productionIds,
  reservedResourceIds,
  reservationId,
  billIds,
  documentIds,
}: {
  tenantId: string
  propertyId: string
  productionIds: string[]
  reservedResourceIds: string[]
  reservationId: string
  billIds: string[]
  documentIds: string[]
}) {
  const showError = useErrorSnackBar()

  const { data: deleteReservedResourceIdsData, loading: deleteReservedResourceIdsLoading } = useDeleteReservedResourceIdsQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        reservationId: { eq: reservationId },
      },
    },
  })
  const deleteReservedResourceIds = deleteReservedResourceIdsData?.reservedResources?.map((reservedResource) => reservedResource.id) ?? []
  const allReservedResourceIds = [...reservedResourceIds, ...deleteReservedResourceIds]

  const { data: reservedResourceAuditData, loading: reservedResourceAuditLoading } = useAuditsListQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId,
        propertyId,
        entityId: { in: allReservedResourceIds },
        entityName: { eq: 'ReservedResource' },
      },
    },
    skip: deleteReservedResourceIdsLoading,
  })

  const { data: deleteProductionIdsData, loading: deleteProductionIdsLoading } = useDeleteProductionIdsQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        reservedResourceId: { in: allReservedResourceIds },
      },
    },
  })
  const deleteProductionIds = deleteProductionIdsData?.productions?.map((production) => production.id) ?? []

  const { data: productionsAuditData, loading: productionsAuditLoading } = useAuditsListQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId,
        propertyId,
        entityId: { in: [...productionIds, ...deleteProductionIds] },
        entityName: { eq: 'Production' },
      },
    },
    skip: deleteProductionIdsLoading,
  })

  // const { data: reservationAuditData, loading: reservationAuditLoading } = useAuditsListQuery({
  //   onError: showError,
  //   variables: {
  //     filter: {
  //       tenantId,
  //       propertyId,
  //       entityId: { eq: reservationId },
  //       entityName: { eq: 'Reservation' },
  //     },
  //   },
  // })

  const { data: deleteBillIdsData, loading: deleteBillIdsLoading } = useDeleteBillIdsQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
        reservationId: { eq: reservationId },
      },
    },
  })
  const deleteBillIds = deleteBillIdsData?.bills?.map((bill) => bill.id) ?? []

  const { data: billAuditData, loading: billAuditLoading } = useAuditsListQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId,
        propertyId,
        entityId: { in: [...billIds, ...deleteBillIds] },
        entityName: { eq: 'Bill' },
      },
    },
    skip: deleteBillIdsLoading,
  })

  const { data: deleteDepositDocumentIdsData, loading: deleteDepositDocumentIdsLoading } = useDeleteDepositDocumentIdsQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        reservationId,
      },
    },
  })
  const deleteDepositDocumentIds = deleteDepositDocumentIdsData?.depositDocuments?.map((depositDocument) => depositDocument.id) ?? []

  const documentIdsFromDeleteBill =
    billAuditData?.audits?.map((audit) => {
      const changes = getChanges({ audit, audits: billAuditData?.audits ?? [], changesToGet: ['documentId'] }) as { documentId: string | undefined } | undefined

      return changes?.documentId
    }) ?? []
  const allDeletedDocumentIds = [...new Set([...documentIds, ...documentIdsFromDeleteBill, ...deleteDepositDocumentIds])].filter(
    (documentId) => documentId !== null && documentId !== undefined,
  ) as string[]

  // const { data: deleteDocumentIdsData, loading: deleteDocumentIdsLoading } = useDeleteDocumentIdsQuery({
  //   onError: showError,
  //   variables: {
  //     filter: {
  //       tenantId: { eq: tenantId },
  //       propertyId: { eq: propertyId },
  //       id: { in: allDeletedDocumentIds },
  //     },
  //   },
  //   skip: billAuditLoading,
  // })
  // const deleteDocumentIds = deleteDocumentIdsData?.documents?.map((document) => document.id) ?? []

  const { data: documentAuditData, loading: documentAuditLoading } = useAuditsListQuery({
    onError: showError,
    variables: {
      filter: {
        tenantId,
        propertyId,
        entityId: { in: allDeletedDocumentIds /* [...documentIds, ...deleteDocumentIds] */ },
        entityName: { eq: 'Document' },
      },
    },
    skip: /* deleteDocumentIdsLoading ||  */ billAuditLoading || deleteBillIdsLoading || deleteDepositDocumentIdsLoading,
  })

  const { data: userData, loading: userLoading } = useUsersQuery({
    variables: {
      filter: {
        propertyId: propertyId,
        tenantId: tenantId,
      },
    },
    onError: showError,
  })
  const users = userData?.users ?? []

  const { data: chargeCategoriesData, loading: chargeCategoryLoading } = useChargeCategoriesAuditQuery({
    variables: {
      filter: {
        propertyId: { eq: propertyId },
        tenantId: { eq: tenantId },
      },
    },
    onError: showError,
  })
  const chargeCategories = chargeCategoriesData?.chargeCategories ?? []

  const { data: resourceTypesData, loading: resourceTypesLoading } = useResourceTypeAuditQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
        propertyId: { eq: propertyId },
      },
    },
    onError: showError,
  })
  const resourceTypes = resourceTypesData?.resourceTypes ?? []

  const { data: vatsData, loading: vatsLoading } = useVatsAuditQuery({
    variables: {
      filter: {
        tenantId: { eq: tenantId },
      },
    },
    onError: showError,
  })
  const vats = vatsData?.vats ?? []

  const { data: reservationStatesData, loading: reservationStatesLoading } = useReservationStatesQuery({
    variables: {
      tenantId,
    },
  })
  const reservationStates = reservationStatesData?.reservationStates ?? []

  const audits = [
    ...(productionsAuditData?.audits ?? []),
    ...(reservedResourceAuditData?.audits ?? []) /* , ...(reservationAuditData?.audits ?? []) */,
    ...(billAuditData?.audits ?? []),
    ...(documentAuditData?.audits ?? []),
  ]?.sort((first, second) => {
    return second.timestamp.getTime() - first.timestamp.getTime()
  })

  return {
    audits,
    users,
    chargeCategories,
    resourceTypes,
    vats,
    reservationStates,
    loading:
      productionsAuditLoading ||
      reservedResourceAuditLoading /* || reservationAuditLoading */ ||
      billAuditLoading ||
      documentAuditLoading ||
      userLoading ||
      chargeCategoryLoading ||
      resourceTypesLoading ||
      vatsLoading ||
      reservationStatesLoading ||
      deleteReservedResourceIdsLoading ||
      deleteProductionIdsLoading ||
      // deleteDocumentIdsLoading ||
      deleteBillIdsLoading ||
      deleteDepositDocumentIdsLoading,
  }
}
